import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import RelativeTime from 'dayjs/plugin/relativeTime';
import UTC from 'dayjs/plugin/utc';
import { t } from 'i18next';
import { ACTIVITY_LOCATION_LOGS_SETTINGS, ACTIVITY_LOG_FUNCTIONS, ACTIVITY_LOG_STATUS, ACTIVITY_LOGS_TYPES, DATE_FORMAT, EDGE_CONNECTION_PREFERENCES, EDGE_CONNECTION_STATUS_CODES, EDGE_CONNECTION_TYPES, KEX_LOGS_UPDATE_MESSAGES, KEX_LOGS_UPDATES, LED_STATES } from 'vio-constants';
import { buildContactName } from 'vio-libs';
dayjs.extend(UTC);
dayjs.extend(RelativeTime);
dayjs.extend(LocalizedFormat);
export function displayEmails(users) {
    const firstUser = users[0];
    const usersLen = users.length;
    if (usersLen === 1) {
        return firstUser;
    }
    const secondUser = users[1];
    if (usersLen === 2) {
        return `${firstUser} and ${secondUser}`;
    }
    const remainingUsers = usersLen - 2;
    return `${firstUser}, ${secondUser}, and ${remainingUsers} other user(s)`;
}
export function getActivityLogMessage(args, functionName, data) {
    switch (functionName) {
        case ACTIVITY_LOG_FUNCTIONS.CREATE_CROSS_CONNECTION:
            return getCreateCrossConnectionLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.CREATE_EDGE_CONNECTION:
            return getCreateEdgeConnectionLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.CREATE_EDGE_OFFERING:
            return getCreateEdgeOfferingLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.CREATE_KEX_PROFILE:
            return getCreateKEXProfileLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.CREATE_ROLE:
            return getCreateRoleLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.INVITE_USER:
            return getInviteUserLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.PATCH_USER:
            if (args) {
                const { action } = args;
                if (action === ACTIVITY_LOG_FUNCTIONS.REMOVE_USERS) {
                    return getRemoveUsersLogMessage(args);
                }
                else if (action === ACTIVITY_LOG_FUNCTIONS.SET_ROLES) {
                    return getSetRolesMessage(args, data);
                }
            }
            break;
        case ACTIVITY_LOG_FUNCTIONS.REMOVE_CROSS_CONNECTION:
            return getRemoveCrossConnectionLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.REMOVE_EDGE_CONNECTION:
            return getRemoveEdgeConnectionLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.REMOVE_EDGE_OFFERING:
            return getRemoveEdgeOfferingLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.REMOVE_KEX_PROFILE:
            return getRemoveKEXProfileLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.REMOVE_ROLE:
            return getRemoveRoleLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.SET_DEVICE_VALUE:
            return getSetDeviceLogMessage(args, data);
        case ACTIVITY_LOG_FUNCTIONS.UPDATE_CROSS_CONNECTION:
            return getUpdateCrossConnectionLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.UPDATE_EDGE_CONNECTION:
            return getUpdateEdgeConnectionLogMessage(args, data);
        case ACTIVITY_LOG_FUNCTIONS.UPDATE_EDGE_OFFERING:
            return getUpdateEdgeOfferingLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.UPDATE_KEX_PROFILE:
            return getUpdateKEXProfileLogMessage(data);
        case ACTIVITY_LOG_FUNCTIONS.UPDATE_PORT_ALIAS:
            return getUpdatePortAliasLogMessage(args);
        case ACTIVITY_LOG_FUNCTIONS.UPDATE_ROLE:
            return getUpdateRoleLogMessage(data);
        default:
            return;
    }
}
export function getActivityLogDateAndTime(timestamp, forAPI) {
    if (timestamp) {
        const utcTime = dayjs.utc(timestamp).toDate();
        return dayjs(utcTime).local().format(forAPI ? DATE_FORMAT.SITE_LOGS_PAYLOAD : DATE_FORMAT.DATE_AND_TIME);
    }
    return;
}
export function getActivityLogTime(timestamp) {
    if (timestamp) {
        const utcTime = dayjs.utc(timestamp).toDate();
        return dayjs(utcTime).local().fromNow();
    }
    return;
}
export function getCreateCrossConnectionLogMessage(args) {
    if (args) {
        const { description } = args;
        return `created the cross connection ${description}`;
    }
    return 'created a cross connection';
}
export function getCreateEdgeConnectionLogMessage(data) {
    if (data) {
        const { edge_offering } = data;
        const { name } = edge_offering;
        return `requested to connect to ${name}`;
    }
    return 'requested to connect to an edge offering';
}
export function getCreateEdgeOfferingLogMessage(args) {
    if (args) {
        const { name } = args;
        return `created the edge offering ${name}`;
    }
    return 'created an edge offering';
}
export function getCreateKEXProfileLogMessage(args) {
    if (args) {
        const { name } = args;
        return `created the KEX profile ${name}`;
    }
    return 'created a KEX profile';
}
export function getCreateRoleLogMessage(args) {
    if (args) {
        const { name } = args;
        return `created the role ${name}`;
    }
    return 'created a new role';
}
export function getInviteUserLogMessage(args) {
    if (args) {
        const { email } = args;
        if (email) {
            return `invited ${email} to the portal`;
        }
    }
    return 'invited a user to the portal';
}
export function getRemoveCrossConnectionLogMessage(data) {
    if (data) {
        const { description } = data;
        return `removed the cross connection ${description}`;
    }
    return 'removed a cross connection';
}
export function getRemoveEdgeConnectionLogMessage(data) {
    if (data) {
        const { edge_offering } = data;
        const { name } = edge_offering;
        return `withdrew connection from ${name}`;
    }
    return 'removed an edge connection';
}
export function getRemoveEdgeOfferingLogMessage(data) {
    if (data) {
        const { edge_offering } = data;
        const { name } = edge_offering;
        return `removed the edge offering ${name}`;
    }
    return 'removed an edge offering';
}
export function getRemoveKEXProfileLogMessage(data) {
    if (data) {
        const { kex_profile } = data;
        const { name } = kex_profile;
        return `removed the KEX profile ${name}`;
    }
    return 'removed a KEX profile';
}
export function getRemoveRoleLogMessage(data) {
    if (data) {
        const { role } = data;
        const { name } = role;
        return `removed the role ${name}`;
    }
    return 'removed a role';
}
export function getRemoveUsersLogMessage(args) {
    if (args) {
        const { users } = args;
        if (users && users.length > 0) {
            return `removed ${displayEmails(users)} from the portal`;
        }
    }
    return 'removed a user from the portal';
}
export function getSetColorMessage(args, data) {
    if (args && data) {
        const { data: value } = args;
        const { device_location } = data;
        if (device_location) {
            return ` #${value.toUpperCase()} on ${device_location.toUpperCase()}`;
        }
    }
    return;
}
export function getSetLEDStateMessage(state, rackId) {
    if (state === LED_STATES.BLINK) {
        return `set LED to BLINK on ${rackId}`;
    }
    else if (state === LED_STATES.OFF) {
        return `turned OFF LED on ${rackId}`;
    }
    return `turned ON LED on ${rackId}`;
}
export function getSetDeviceLogMessage(args, data) {
    if (args && data) {
        const { action, data: value } = args;
        const { device_location, device_position, portal_display_name: lockerDisplayName } = data;
        if (device_location !== undefined && device_position !== undefined) {
            const lockerId = device_position.toUpperCase();
            const rackId = device_location.toUpperCase();
            switch (action) {
                case ACTIVITY_LOCATION_LOGS_SETTINGS.COLOR:
                    return 'set color to ';
                case ACTIVITY_LOCATION_LOGS_SETTINGS.LOCK:
                    if (lockerDisplayName) {
                        const rackNumber = rackId.match(/\d+/g)[0];
                        return `locked ${lockerDisplayName}, ${t('RACK', {
                            ns: 'Common'
                        })} ${rackNumber}`;
                    }
                    return `locked ${lockerId}.${rackId}`;
                case ACTIVITY_LOCATION_LOGS_SETTINGS.STATE:
                    return getSetLEDStateMessage(value, rackId);
                case ACTIVITY_LOCATION_LOGS_SETTINGS.UNLOCK:
                    if (lockerDisplayName) {
                        const rackNumber = rackId.match(/(\d+)/)[0];
                        return `unlocked ${lockerDisplayName}, ${t('RACK', {
                            ns: 'Common'
                        })} ${rackNumber}`;
                    }
                    return `unlocked ${lockerId}.${rackId}`;
                default:
                    break;
            }
        }
        if (action.includes('"rack":')) {
            const rackNum = action.match(/\d+/)[0];
            const rackId = `R${rackNum}`;
            return `rotated ${rackId} to front position`;
        }
    }
    return 'changed a device setting';
}
export function getSetRolesMessage(args, data) {
    const { email } = args;
    if (data) {
        const { new_roles, removed_roles, updated_roles } = data;
        if (new_roles && removed_roles && updated_roles) {
            const newRolesLen = new_roles.length;
            const removedRolesLen = removed_roles.length;
            const updatedRolesLen = updated_roles.length;
            const newRoles = new_roles.map(role => {
                const { name } = role;
                return name;
            }).join(', ');
            const removedRoles = removed_roles.join(', ');
            const updatedRoles = updated_roles.map(role => {
                const { name } = role;
                return name;
            }).join(', ');
            if (newRolesLen > 0 && removedRolesLen > 0 && updatedRolesLen > 0) {
                return `revoked ${removedRoles} access, updated markets for ${updatedRoles} access, and granted ${newRoles} access to ${email}`;
            }
            if (removedRolesLen > 0 && updatedRolesLen > 0) {
                return `revoked ${removedRoles} access, and updated markets for ${updatedRoles} access for ${email}`;
            }
            if (newRolesLen > 0 && updatedRolesLen > 0) {
                return `updated markets for ${updatedRoles} access, and granted ${newRoles} access to ${email}`;
            }
            if (newRolesLen > 0 && removedRolesLen > 0) {
                return `revoked ${removedRoles} access, and granted ${newRoles} access to ${email}`;
            }
            if (newRolesLen > 0) {
                return `granted ${newRoles} access to ${email}`;
            }
            if (removedRolesLen > 0) {
                return `revoked ${removedRoles} access from ${email}`;
            }
            if (updatedRolesLen > 0) {
                return `updated markets for ${updatedRoles} access for ${email}`;
            }
        }
    }
    return `updated roles for ${email}`;
}
export function getUpdateCrossConnectionLogMessage(args) {
    if (args) {
        const { description } = args;
        return `updated the name of the cross connection ${description}`;
    }
    return 'updated the name of a cross connection';
}
export function getUpdateEdgeConnectionLogMessage(args, data) {
    if (args && data) {
        const { edge_offering } = data;
        const { name: edgeOfferingName } = edge_offering;
        const { edge_connection_status: edgeConnectionStatus } = args;
        const { PENDING_DEACTIVATION } = EDGE_CONNECTION_STATUS_CODES;
        if (edgeConnectionStatus === PENDING_DEACTIVATION) {
            return `requested to deactivate the connection from ${edgeOfferingName}`;
        }
        return `modified the connection from ${edgeOfferingName}`;
    }
    return 'modified an edge connection';
}
export function getUpdateEdgeOfferingLogMessage(data) {
    if (data) {
        const { edge_offering, updates } = data;
        const { name: edgeOfferingName } = edge_offering;
        const updatedData = Object.keys(updates);
        if (updatedData.length === 0) {
            return `updated the offering ${edgeOfferingName}`;
        }
        if (updatedData.length === 1) {
            switch (updatedData[0]) {
                case KEX_LOGS_UPDATES.CONNECTION_PREFERENCE:
                    {
                        const { connection_preference } = updates;
                        const edgeConnectionPreference = EDGE_CONNECTION_PREFERENCES[connection_preference];
                        return `set the connection preference of the offering ${edgeOfferingName} to ${edgeConnectionPreference}`;
                    }
                case KEX_LOGS_UPDATES.CONNECTION_TYPE:
                    {
                        const { connection_type } = updates;
                        const edgeConnectionType = EDGE_CONNECTION_TYPES[connection_type];
                        return `set the connection type of the offering ${edgeOfferingName} to ${edgeConnectionType}`;
                    }
                case KEX_LOGS_UPDATES.CONTACTS:
                    {
                        const { contacts } = updates;
                        const { added_contacts, removed_contacts } = contacts;
                        const addedContactsLen = added_contacts.length;
                        const removedContactsLen = removed_contacts.length;
                        const addedContacts = added_contacts.map(element => {
                            const name = buildContactName(element);
                            return name;
                        });
                        const removedContacts = removed_contacts.map(element => {
                            const name = buildContactName(element);
                            return name;
                        });
                        if (addedContactsLen > 0 && removedContactsLen > 0) {
                            const added = addedContacts.join(', ');
                            const removed = removedContacts.join(', ');
                            return `removed ${removed} and added ${added} to the offering ${edgeOfferingName}'s contacts`;
                        }
                        if (addedContactsLen > 0) {
                            const added = addedContacts.join(', ');
                            return `added ${added} to the offering ${edgeOfferingName}'s contacts`;
                        }
                        if (removedContactsLen > 0) {
                            const removed = removedContacts.join(', ');
                            return `removed ${removed} from the offering ${edgeOfferingName}'s contacts`;
                        }
                        return `updated the offering ${edgeOfferingName}'s contacts`;
                    }
                case KEX_LOGS_UPDATES.DESCRIPTION:
                    return `updated the description of the offering ${edgeOfferingName}`;
                case KEX_LOGS_UPDATES.EDGE_MARKET:
                    {
                        const { edge_market } = updates;
                        return `updated the edge market of the offering ${edgeOfferingName} to ${edge_market}`;
                    }
                case KEX_LOGS_UPDATES.NAME:
                    return `updated the name of the offering ${edgeOfferingName}`;
                case KEX_LOGS_UPDATES.PORTS:
                    {
                        const { ports } = updates;
                        const { added_ports, removed_ports } = ports;
                        const addedPortsLen = added_ports.length;
                        const removedPortsLen = removed_ports.length;
                        if (addedPortsLen > 0 && removedPortsLen > 0) {
                            const added = added_ports.join(', ');
                            const removed = removed_ports.join(', ');
                            return `removed ${removed} and added ${added} to the offering ${edgeOfferingName}'s ports`;
                        }
                        if (addedPortsLen > 0) {
                            const added = added_ports.join(', ');
                            return `added ${added} to the offering ${edgeOfferingName}'s ports`;
                        }
                        if (removedPortsLen > 0) {
                            const removed = removed_ports.join(', ');
                            return `removed ${removed} from the offering ${edgeOfferingName}'s ports`;
                        }
                        return `updated the offering ${edgeOfferingName}'s ports`;
                    }
                default:
                    return undefined;
            }
        }
        const updatedInfo = updatedData.map(element => {
            const update = element.toUpperCase();
            return KEX_LOGS_UPDATE_MESSAGES[update];
        }).join(', ');
        return `updated the ${updatedInfo} of the offering ${edgeOfferingName}`;
    }
    return 'updated an edge offering';
}
export function getUpdateKEXProfileLogMessage(data) {
    if (data) {
        const { kex_profile, updates } = data;
        const { name: kexProfileName } = kex_profile;
        const updatedData = Object.keys(updates);
        if (updatedData.length === 0) {
            return `updated the profile ${kexProfileName}`;
        }
        if (updatedData.length === 1) {
            switch (updatedData[0]) {
                case KEX_LOGS_UPDATES.BIO:
                    return `updated the bio of the KEX profile ${kexProfileName}`;
                case KEX_LOGS_UPDATES.CONTACTS:
                    {
                        const { contacts } = updates;
                        const { added_contacts, removed_contacts } = contacts;
                        const addedContactsLen = added_contacts.length;
                        const removedContactsLen = removed_contacts.length;
                        const addedContacts = added_contacts.map(element => {
                            const name = buildContactName(element);
                            return name;
                        });
                        const removedContacts = removed_contacts.map(element => {
                            const name = buildContactName(element);
                            return name;
                        });
                        if (addedContactsLen > 0 && removedContactsLen > 0) {
                            const added = addedContacts.join(', ');
                            const removed = removedContacts.join(', ');
                            return `removed ${removed} and added ${added} to the profile ${kexProfileName}'s contacts`;
                        }
                        if (addedContactsLen > 0) {
                            const added = addedContacts.join(', ');
                            return `added ${added} to the profile ${kexProfileName}'s contacts`;
                        }
                        if (removedContactsLen > 0) {
                            const removed = removedContacts.join(', ');
                            return `removed ${removed} from the profile ${kexProfileName}'s contacts`;
                        }
                        return `updated the profile ${kexProfileName}'s contacts`;
                    }
                case KEX_LOGS_UPDATES.LOGO_URL:
                    return `updated the company image of the profile ${kexProfileName}`;
                case KEX_LOGS_UPDATES.NAME:
                    return `updated the name of the profile ${kexProfileName}`;
                case KEX_LOGS_UPDATES.URL:
                    return `updated the company url of the profile ${kexProfileName}`;
                case KEX_LOGS_UPDATES.VISIBILITY:
                    {
                        const { visibility } = updates;
                        const visibilityMessage = visibility ? 'PUBLIC' : 'PRIVATE';
                        return `set the visibility of the profile ${kexProfileName} to ${visibilityMessage}`;
                    }
                default:
                    return;
            }
        }
        const updatedInfo = updatedData.map(element => {
            const update = element.toUpperCase();
            return KEX_LOGS_UPDATE_MESSAGES[update];
        }).join(', ');
        return `updated the ${updatedInfo} of the profile ${kexProfileName}`;
    }
    return 'updated a KEX profile';
}
export function getUpdatePortAliasLogMessage(args) {
    if (args) {
        const { description, device_id, interface_id } = args;
        const interfaceName = `${interface_id}.${device_id}`;
        if (description && description.length > 0) {
            return `set alias "${description}" for ${interfaceName}`;
        }
        return `cleared alias for ${interfaceName}`;
    }
    return 'updated a network interface alias';
}
export function getUpdateRoleLogMessage(data) {
    if (data) {
        const { role, updates } = data;
        const { name: roleName } = role;
        const { authorizations, description, name } = updates;
        if (authorizations && !description && !name) {
            return `updated authorizations for ${roleName}`;
        }
        if (!authorizations && description && !name) {
            return `updated description for ${roleName}`;
        }
        if (!authorizations && !description && name) {
            const { new: newRoleName } = name;
            return `updated name of the ${roleName} to ${newRoleName}`;
        }
        if (name) {
            const { new: newRoleName } = name;
            return `updated the role ${newRoleName}`;
        }
        return `updated the role ${roleName}`;
    }
    return 'updated a role';
}
export function isActivityLocationLogs(type) {
    if (type === ACTIVITY_LOGS_TYPES.LOCATION || type === ACTIVITY_LOGS_TYPES.NETWORK_INTERFACES) {
        return true;
    }
    return false;
}
export function isActivityLogCompleted(status) {
    if (status === ACTIVITY_LOG_STATUS.COMPLETED) {
        return true;
    }
    return false;
}
export function isLEDColorSetting(setting) {
    if (setting === ACTIVITY_LOCATION_LOGS_SETTINGS.COLOR) {
        return true;
    }
    return false;
}
